import "../index.css";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardContent,
  Column,
  IconButton,
  LabelValue,
  Row,
  Tooltip,
  Typography,
} from "@/ui-kit";
import { ImageGallery } from "@/components/ImageGallery";
import { PencilIcon } from "lucide-react";
import { AddressUtils } from "@/utils/address";
import type { ILender } from "@/types/companies-lenders";
import type { IContractingCompany } from "@/types/companies-contractors";
import { PhoneUtils } from "@/utils/phone";
import type { ReactNode } from "react";

type IProps = {
  company: ILender | IContractingCompany | undefined;
  banner?: ReactNode;
  onEdit?: () => void;
};

export const CompanyDetailsCard = ({ company, banner, onEdit }: IProps) => {
  const { t } = useTranslation();
  const labelWidth = "60px";

  return (
    <Card className="profile-card" variant="layer1">
      <CardContent>
        <Column gap={24} fullWidth>
          {banner}
          <Row gap={24} alignmentVertical="top">
            <div className="profile-card__image-gallery">
              <ImageGallery imageUrls={company ? [company.companyLogo] : []} />
            </div>
            <Column gap={12} fullWidth>
              <Row alignmentHorizontal="justify">
                <Typography
                  as="h3"
                  weight="bold"
                  variant="h3"
                  color="text-primary"
                  text={t("label.company")}
                />
                {onEdit && (
                  <div className="profile-card__actions">
                    <Tooltip content={t("action.edit")} placement="bottom-end">
                      <IconButton
                        size="sm"
                        variant="ghost"
                        color="layer2"
                        content={<PencilIcon />}
                        onClick={onEdit}
                        aria-label={t("action.edit")}
                      />
                    </Tooltip>
                  </div>
                )}
              </Row>

              <Column gap={4}>
                <LabelValue
                  label={t("label.name")}
                  value={company?.name}
                  labelWidth={labelWidth}
                />
                <LabelValue
                  label={t("label.address")}
                  value={AddressUtils.format(company?.address)}
                  labelWidth={labelWidth}
                  canCopy
                />
                <LabelValue
                  label={t("label.phone")}
                  value={PhoneUtils.format(company?.phone)}
                  labelWidth={labelWidth}
                  canCopy
                />
                <LabelValue
                  label={t("label.email")}
                  value={company?.email}
                  labelWidth={labelWidth}
                  canCopy
                />
              </Column>
            </Column>
          </Row>
        </Column>
      </CardContent>
    </Card>
  );
};
